import { type FC, useState } from "react"
import { Navigate, useParams } from "react-router"
import styled from "styled-components"

import { parseNumber } from "@forento/shared/utilities/number"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { type PaypalOrder, PurchaseSuccessModalContextProvider } from "~/contexts/PurchaseSuccessModalContext"
import { useUser } from "~/contexts/UserContext"
import Button from "~/themes/school/components/Button"
import Hero from "~/themes/school/components/Hero"
import { ArrowIcon } from "~/themes/school/components/Icon"
import Layout from "~/themes/school/components/Layout"
import Modal, { ModalButtons, ModalTitle } from "~/themes/school/components/Modal"
import { PageBreadcrumb } from "~/themes/school/components/PageHeader"
import PartialLoadingPage from "~/themes/school/components/PartialLoadingPage"
import PurchaseItem from "~/themes/school/components/PurchaseItem"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

const SessionPage: FC = () => {
	const sessionId = parseNumber(useParams().sessionId!)
	const user = useUser()
	const t = useTranslation()

	const [isModalOpen, setModalOpen] = useState(false)
	const [paypalOrder, setPaypalOrder] = useState<PaypalOrder | null>(null)

	const { data: session, error, refetch } = query.privateSession.getPersonalized.useQuery(sessionId ?? -1)

	if (session === null) return <Navigate to={routes.session.index()} />

	const redemotionInstructions = session?.status === "unlocked" ? session.redemptionInstructions : null
	const redemptionLink = session?.status === "unlocked" ? session.redemptionLink : null

	return (
		<Layout>
			<Modal isOpen={isModalOpen}>
				<ModalTitle>{t("session.redeem")}</ModalTitle>
				<p>{redemotionInstructions}</p>
				<ModalButtons>
					{redemptionLink !== null ? (
						<>
							<Button variant="secondary" onClick={() => setModalOpen(false)}>
								{t("cancel")}
							</Button>
							<Button variant="primary" onClick={redemptionLink} newTab>
								{t("session.redeem")}
							</Button>
						</>
					) : (
						<Button variant="primary" onClick={() => setModalOpen(false)}>
							{t("close")}
						</Button>
					)}
				</ModalButtons>
			</Modal>
			{error ? (
				<p>{t("failedToLoad")}</p>
			) : session === undefined ? (
				<PartialLoadingPage />
			) : (
				<PurchaseSuccessModalContextProvider content={{ type: "session" }} paypalOrder={paypalOrder}>
					<PageBreadcrumb
						path={[{ title: t("session.title"), link: routes.session.index() }]}
						title={session.title}
					/>
					<Hero
						title={session.title}
						htmlDescription={session.longDescription?.html}
						thumbnailUrl={session.thumbnailFilePath}
						callToAction={
							session.status === "paywall" ? (
								<PurchaseItem
									item={{
										type: "session",
										id: session.id,
										name: session.title,
										shortDescription: session.shortDescription,
										price: session.price,
										accessTimeMonths: null,
									}}
									onPurchase={async ({ paypalOrder }) => {
										setPaypalOrder(paypalOrder)
										await refetch()
									}}
								/>
							) : session.status === "sign-in-required" ? (
								<CallToAction>
									<p>{t("product.onlyForUsers")}</p>
									<ButtonsRow>
										<Button variant="primary" onClick={routes.user.signin()}>
											{t("user.signIn.title")}
										</Button>
										<Button variant="primary" onClick={routes.user.signup()}>
											{t("user.signUp.title")}
										</Button>
									</ButtonsRow>
								</CallToAction>
							) : session.status === "membership-required" ? (
								<CallToAction>
									<p>{t("product.onlyForMembers")}</p>
									<ButtonsRow>
										<Button variant="primary" onClick={routes.membership.index()}>
											{t("membership.title")}
										</Button>
									</ButtonsRow>
								</CallToAction>
							) : session.status === "bokadero-membership-required" ? (
								<CallToAction>
									<p>Gratis för medlemmar i Bokadero Arena</p>
									<ButtonsRow>
										{user.user ? (
											<Button variant="primary" onClick={routes.user.settings()}>
												Hantera inställningar
											</Button>
										) : (
											<Button variant="primary" onClick={routes.user.signup()}>
												Skapa konto
											</Button>
										)}
									</ButtonsRow>
								</CallToAction>
							) : session.status === "unlocked" ? (
								session.redemptionInstructions !== null ? (
									<Button variant="primary" onClick={() => setModalOpen(true)}>
										<CallToActionIcon as={ArrowIcon} />
										{t("session.redeem")}
									</Button>
								) : session.redemptionLink !== null ? (
									<Button variant="primary" onClick={session.redemptionLink} newTab>
										<CallToActionIcon as={ArrowIcon} />
										{t("session.redeem")}
									</Button>
								) : null
							) : session.status === "paused" ? (
								<CallToAction>
									<p>{t("product.paused")}</p>
								</CallToAction>
							) : (
								exhaustiveGuard(session.status)
							)
						}
					/>
				</PurchaseSuccessModalContextProvider>
			)}
		</Layout>
	)
}

const CallToAction = styled.div`
	max-width: 400px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	text-align: center;
`

const ButtonsRow = styled.div`
	display: flex;
	gap: 16px;

	@media (max-width: 499px) {
		flex-direction: column;
	}
`

const CallToActionIcon = styled.div`
	width: 16px;
	height: 16px;
`

export default SessionPage
